/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import { styled } from '@aibi-ui/core';
import { Input as AntdInput, InputNumber as AntdInputNumber } from 'antd';
import { InputProps } from 'antd/lib/input';
import Icons from 'src/components/Icons';
import ClearIcon from '../ClearIcon';
import { forwardRef } from 'react';

const BaseInput = forwardRef<any, InputProps & { showCount?: boolean; showSearch?: boolean }>(
  (props, ref) => {
    const { allowClear, showCount, showSearch, ...rest } = props;
    const { value } = props;
    let count = 0;
    if (typeof value === 'number') {
      count = String(value).length;
    } else {
      count = value?.length || 0;
    }
    const getSuffixIcon = () => {
      if (showCount) {
        return (
          <div css={{ color: 'rgba(0, 0, 0, 0.45)' }}>
            {count} / {props.maxLength}
          </div>
        );
      }
      if (value && allowClear) {
        return (
          <ClearIcon
            onClick={(e: Event) => {
              e.stopPropagation();
              props.onChange?.({ target: { value: '' } } as any);
            }}
          />
        );
      }
      if (showSearch) {
        return (
          <Icons.InputSearch viewBox="0 0 16 16" style={{ fontSize: '16px' }} />
        );
      }
      return <></>;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value;
      if (typeof value === 'string') {
        value = value.trim();
        e.target.value = value;
      }
      props.onChange && props.onChange(e);
    }

    return <AntdInput ref={ref} suffix={getSuffixIcon()} {...rest} onChange={handleChange} />;
  },
)

export const Input = styled(BaseInput)`
  // border: 1px solid ${({ theme }) => theme.colors.secondary.light3};
  border-radius: ${({ theme }) => theme.borderRadius}px;
  .anticon {
    svg {
      fill: ${({ showSearch }) => (showSearch ? 'none' : 'currentColor')};
    }
  }
  :hover,
  :focus {
    border-color: #80a8ff !important;
  }

  .ant-input-disabled {
    color: #909aaa;
  }
`;

export const InputNumber = styled(AntdInputNumber)`
  border: 1px solid ${({ theme }) => theme.colors.secondary.light3};
  border-radius: ${({ theme }) => theme.borderRadius}px;
`;

export const TextArea = styled(AntdInput.TextArea)`
  // border: 1px solid ${({ theme }) => theme.colors.secondary.light3};
  border-radius: ${({ theme }) => theme.borderRadius}px;
`;
